import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Table, Button, Group, Modal, Stack, TextInput, Select,
  Badge, ActionIcon, Tooltip, Text, Box, Alert, Card, Menu, Center,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useBreakpoint } from '../../hooks/useBreakpoint'
import { notifications } from '@mantine/notifications'
import { IconPlus, IconEdit, IconTrash, IconLock, IconSearch, IconDotsVertical } from '@tabler/icons-react'
import { getWoCategories, createWoCategory, updateWoCategory, deleteWoCategory } from '../../api/client'
import { useWorkOrderMeta } from '../../hooks/useWorkOrderMeta'
import ImportExportButtons from './ImportExportButtons'

export default function WoCategoriesTab() {
  const { t } = useTranslation()
  const { isMobile } = useBreakpoint()
  const { departmentOptions, deptLabel, catLabel } = useWorkOrderMeta()
  const APPLIES_TO_OPTIONS = [
    { value: '',    label: t('config.woCatAppliesToBoth') },
    { value: 'WO',  label: t('config.woCatAppliesToWO') },
    { value: 'HKO', label: t('config.woCatAppliesToHKO') },
  ]
  const [cats, setCats] = useState<any[]>([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<any>(null)
  const [form, setForm] = useState({ code: '', name: '', nameTh: '', appliesTo: '', departmentCode: '' })
  const [opened, { open, close }] = useDisclosure(false)
  const [saving, setSaving] = useState(false)

  const load = async () => {
    setLoading(true)
    try {
      const { data } = await getWoCategories()
      setCats(data ?? [])
    } finally { setLoading(false) }
  }

  useEffect(() => { load() }, [])

  const handleOpen = (cat?: any) => {
    setEditing(cat ?? null)
    setForm(cat ? { code: cat.code, name: cat.name, nameTh: cat.nameTh ?? '', appliesTo: cat.appliesTo ?? '', departmentCode: cat.departmentCode ?? '' } : { code: '', name: '', nameTh: '', appliesTo: '', departmentCode: '' })
    open()
  }

  const handleSave = async () => {
    if (!form.name.trim()) return
    setSaving(true)
    try {
      if (editing) {
        await updateWoCategory(editing.id, { name: form.name, nameTh: form.nameTh || null, appliesTo: form.appliesTo || null, departmentCode: form.departmentCode || null })
        notifications.show({ color: 'teal', message: `✅ ${t('config.edit')} "${form.name}"` })
      } else {
        if (!form.code.trim()) return notifications.show({ color: 'red', message: t('config.woCatCodeRequired', 'Please enter a Code') })
        await createWoCategory({ code: form.code.toUpperCase(), name: form.name, nameTh: form.nameTh || undefined, appliesTo: form.appliesTo || null, departmentCode: form.departmentCode || undefined })
        notifications.show({ color: 'teal', message: `✅ ${t('config.add')} "${form.name}"` })
      }
      close(); load()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? t('common.error', 'เกิดข้อผิดพลาด') })
    } finally { setSaving(false) }
  }

  const handleDelete = async (cat: any) => {
    if (!confirm(t('config.woCatDeleteConfirm'))) return
    try {
      await deleteWoCategory(cat.id)
      notifications.show({ color: 'teal', message: t('config.woCatDeletedMsg', 'Deleted "{{name}}"', { name: cat.name }) })
      load()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? t('common.error', 'เกิดข้อผิดพลาด') })
    }
  }

  const filtered = cats.filter(cat => {
    const q = search.toLowerCase()
    return !q || cat.code?.toLowerCase().includes(q) || cat.name?.toLowerCase().includes(q) || cat.nameTh?.toLowerCase().includes(q)
      || (cat.departmentCode && (cat.departmentCode.toLowerCase().includes(q) || deptLabel(cat.departmentCode).toLowerCase().includes(q)))
  })

  const appliesToLabel = (cat: any) =>
    cat.appliesTo === 'WO' ? t('config.woCatAppliesToWO', 'Work Order')
    : cat.appliesTo === 'HKO' ? t('config.woCatAppliesToHKO', 'HKO')
    : t('config.woCatAppliesToBoth', 'Both')

  return (
    <Box>
      <Group justify="space-between" mb="sm" wrap="wrap">
        <Group gap="sm" />
        <Group gap="sm" wrap="wrap">
          <TextInput
            placeholder={t('config.woCatSearchPlaceholder', 'Search code or name...')}
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="sm"
            style={{ width: isMobile ? '100%' : 200 }}
          />
          <Button leftSection={<IconPlus size={16} />} onClick={() => handleOpen()}
            style={{ background: 'var(--brand-primary,#0f172a)', color: '#fff', fontWeight: 500 }}>
            {t('config.addWoCategory', 'Add Category')}
          </Button>
          <ImportExportButtons resource="wo-categories" onImported={load} />
        </Group>
      </Group>

      <Alert color="blue" variant="light" mb="xs">
        {t('config.woCatDefaultAlert')}
      </Alert>
      <Alert color="gray" variant="light" mb="md">
        {t('config.woCatImportHint', 'Import columns: code, name, nameTh, appliesTo (WO / HKO / blank = both), departmentCode (must match a code in Departments). Download the template for a ready-made example.')}
      </Alert>

      {isMobile ? (
        // ── มือถือ: การ์ดตาม pattern เดียวกับ CrudTable ใน ConfigurationPage.tsx ──
        filtered.length === 0 ? (
          <Center p="xl"><Text c="dimmed" size="sm">{loading ? '' : t('config.woCatEmpty', 'No work order categories yet')}</Text></Center>
        ) : (
          <Stack gap="xs">
            {filtered.map(cat => (
              <Card key={cat.id} withBorder radius="lg" p="md">
                <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
                  <Box style={{ minWidth: 0, flex: 1 }}>
                    <Group gap={6} mb={4}>
                      <Text size="sm" ff="monospace" fw={700}>{cat.code}</Text>
                      {cat.isDefault && <Badge size="xs" color="orange">{t('config.defaultBadge', 'Default')}</Badge>}
                    </Group>
                    <Text size="sm" fw={600}>{catLabel(cat.code)}</Text>
                    <Text size="xs" c="dimmed" mt={2}>{appliesToLabel(cat)}{cat.departmentCode ? ` · ${deptLabel(cat.departmentCode)}` : ''}</Text>
                  </Box>
                  <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
                    <Badge color={cat.isActive ? 'teal' : 'gray'} variant="light" size="sm">
                      {cat.isActive ? t('config.active', 'Active') : t('config.inactive', 'Inactive')}
                    </Badge>
                    {cat.isDefault ? (
                      <ActionIcon variant="subtle" color="gray" size="lg" disabled><IconLock size={16} /></ActionIcon>
                    ) : (
                      <Menu shadow="md" width={160} position="bottom-end">
                        <Menu.Target>
                          <ActionIcon variant="subtle" color="gray" size="lg"><IconDotsVertical size={18} /></ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item leftSection={<IconEdit size={15} />} onClick={() => handleOpen(cat)}>
                            {t('config.edit', 'Edit')}
                          </Menu.Item>
                          <Menu.Item leftSection={<IconTrash size={15} />} color="red" onClick={() => handleDelete(cat)}>
                            {t('config.deletePerm', 'Delete permanently')}
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    )}
                  </Group>
                </Group>
              </Card>
            ))}
          </Stack>
        )
      ) : (
      <Table highlightOnHover>
        <Table.Thead style={{ background: '#f8f7f4' }}>
          <Table.Tr>
            <Table.Th>{t('config.colCode', 'Code')}</Table.Th>
            <Table.Th>{t('config.woCatNameLabel', 'Category Name')}</Table.Th>
            <Table.Th>{t('config.woCatDepartment', 'Department')}</Table.Th>
            <Table.Th>{t('config.woCatAppliesTo', 'Applies To')}</Table.Th>
            <Table.Th>{t('config.statusHeader', 'Status')}</Table.Th>
            <Table.Th></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {filtered.map(cat => (
            <Table.Tr key={cat.id}>
              <Table.Td>
                <Group gap={4}>
                  <Text size="sm" ff="monospace">{cat.code}</Text>
                  {cat.isDefault && <Badge size="xs" color="orange">{t('config.defaultBadge', 'Default')}</Badge>}
                </Group>
              </Table.Td>
              <Table.Td><Text size="sm">{catLabel(cat.code)}</Text>{cat.nameTh && cat.name && <Text size="xs" c="dimmed">{cat.name} / {cat.nameTh}</Text>}</Table.Td>
              <Table.Td>{cat.departmentCode ? <Badge size="sm" variant="light" color="indigo">{deptLabel(cat.departmentCode)}</Badge> : <Text size="xs" c="dimmed">—</Text>}</Table.Td>
              <Table.Td>
                <Text size="xs" c="dimmed">{appliesToLabel(cat)}</Text>
              </Table.Td>
              <Table.Td>
                <Badge color={cat.isActive ? 'teal' : 'gray'} variant="light" size="sm">
                  {cat.isActive ? t('config.active', 'Active') : t('config.inactive', 'Inactive')}
                </Badge>
              </Table.Td>
              <Table.Td>
                <Group gap={4}>
                  {cat.isDefault ? (
                    <Tooltip label={t('config.woCatDefaultLocked', 'Default category — cannot be edited')}>
                      <ActionIcon variant="subtle" color="gray" disabled>
                        <IconLock size={15} />
                      </ActionIcon>
                    </Tooltip>
                  ) : (
                    <>
                      <Tooltip label={t('config.edit', 'Edit')}>
                        <ActionIcon variant="subtle" color="blue" onClick={() => handleOpen(cat)}>
                          <IconEdit size={15} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t('config.deletePerm', 'Delete permanently')}>
                        <ActionIcon variant="subtle" color="red" onClick={() => handleDelete(cat)}>
                          <IconTrash size={15} />
                        </ActionIcon>
                      </Tooltip>
                    </>
                  )}
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
          {!loading && !cats.length && (
            <Table.Tr><Table.Td colSpan={6}><Text c="dimmed" size="sm" ta="center" py="md">{t('config.woCatEmpty', 'No work order categories yet')}</Text></Table.Td></Table.Tr>
          )}
        </Table.Tbody>
      </Table>
      )}

      <Modal opened={opened} onClose={close} size="sm" fullScreen={isMobile}
        title={<Text fw={600}>{editing ? t('config.woCatEdit') : t('config.woCatAdd')}</Text>}>
        <Stack gap="sm">
          {!editing && (
            <TextInput label={t('config.woCatCodeLabel')} placeholder={t('config.woCatCodePlaceholder')}
              value={form.code} onChange={e => setForm(p => ({ ...p, code: e.target.value.toUpperCase().replace(/\s/g, '_') }))} />
          )}
          <TextInput label={t('config.woCatNameLabel')} placeholder={t('config.woCatNamePlaceholder')}
            value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
          <TextInput label={t('config.woCatNameThLabel', 'Thai name (optional)')} placeholder={t('config.woCatNameThPlaceholder', 'e.g. เครือข่าย / Wi-Fi')}
            value={form.nameTh} onChange={e => setForm(p => ({ ...p, nameTh: e.target.value }))} />
          <Select label={t('config.woCatDepartment', 'Department')} placeholder={t('config.woCatDepartmentPlaceholder', 'Which department handles this type?')}
            clearable searchable nothingFoundMessage={t('workOrders.noMatch')}
            data={departmentOptions} value={form.departmentCode || null}
            onChange={v => setForm(p => ({ ...p, departmentCode: v ?? '' }))} />
          <Select label={t('config.woCatAppliesTo')} data={APPLIES_TO_OPTIONS}
            value={form.appliesTo} onChange={v => setForm(p => ({ ...p, appliesTo: v ?? '' }))} />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={close}>{t('common.cancel', 'Cancel')}</Button>
            <Button loading={saving} onClick={handleSave}
              style={{ background: 'var(--brand-primary,#0f172a)', color: '#fff', fontWeight: 500 }}>
              {t('common.save', 'Save')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  )
}
